/**
 * 전술 편집기 UI 용 어휘집 — 조건·행동의 한글명·파라미터 스펙.
 *
 * 백엔드 docs/engine/tactic-system.md §3-4 와 동기화. 새 kind 추가 시 백엔드 평가기와
 * 같이 확장해야 UI 가 현실적으로 의미 있는 값을 만들어낸다.
 */

export type TacticConditionKind =
    | "always"
    | "selfHpBelow"
    | "selfHpAbove"
    | "selfMpBelow"
    | "selfMpAbove"
    | "selfBuffActive"
    | "selfDebuffActive"
    | "enemyHpBelow"
    | "enemyHpAbove"
    | "enemyHasTag"
    | "enemyCount"
    | "enemyCasting"
    | "distanceAbove"
    | "distanceBelow"
    | "tickFromStart"
    | "combatPhase"
    | "cooldownReady"
    | "allyHpBelow"
    | "and"
    | "or"
    | "not";

export type TacticActionKind =
    | "useSkill"
    | "basicAttack"
    | "moveToward"
    | "moveAway"
    | "holdPosition"
    | "defend"
    | "retreat"
    | "wait";

/** 평탄 구조 — 백엔드 record 와 동일 형태. 빈 필드는 omit. */
export interface TacticCondition {
    kind: TacticConditionKind;
    value?: number;
    amount?: number;
    amountMax?: number;
    skillId?: string;
    tag?: string;
    buffId?: string;
    children?: TacticCondition[];
    inner?: TacticCondition;
}

export interface TacticAction {
    kind: TacticActionKind;
    skillId?: string;
    target?: string;
    ticks?: number;
}

export interface TacticRule {
    id: string;
    when: TacticCondition;
    then: TacticAction;
    comment?: string;
    enabled?: boolean;
}

export interface TacticSet {
    id?: string;
    name?: string;
    doctrineId?: string;
    rules: TacticRule[];
    maxRules?: number;
}

/** 드롭다운 라벨·파라미터 힌트. */
export interface ConditionSpec {
    kind: TacticConditionKind;
    label: string;
    /** 그룹 헤더 — "self" / "enemy" / "position" / "time" / "resource" / "combinator". */
    group: string;
    /** 파라미터 종류. UI 가 렌더할 입력 필드. */
    params: ConditionParamSpec[];
    /** 예시 문구 — 빈 규칙을 만들었을 때 상단에 힌트로 보여줌. */
    hint?: string;
}

export type ConditionParamSpec =
    | { field: "value"; label: string; kind: "ratio" | "distance"; default: number }
    | { field: "amount"; label: string; kind: "integer"; default: number }
    | { field: "skillId"; label: string; kind: "skillPicker"; default: string }
    | { field: "tag"; label: string; kind: "tagPicker"; default: string; options?: string[] }
    | { field: "buffId"; label: string; kind: "text"; default: string };

export const CONDITION_SPECS: ConditionSpec[] = [
    { kind: "always", label: "항상", group: "기본", params: [], hint: "맨 아래 fallback 에 사용" },

    { kind: "selfHpBelow", label: "내 HP 가 __% 이하", group: "자기",
        params: [{ field: "value", label: "비율", kind: "ratio", default: 0.30 }] },
    { kind: "selfHpAbove", label: "내 HP 가 __% 이상", group: "자기",
        params: [{ field: "value", label: "비율", kind: "ratio", default: 0.70 }] },
    { kind: "selfMpBelow", label: "내 MP 가 __% 이하", group: "자기",
        params: [{ field: "value", label: "비율", kind: "ratio", default: 0.30 }] },
    { kind: "selfMpAbove", label: "내 MP 가 __% 이상", group: "자기",
        params: [{ field: "value", label: "비율", kind: "ratio", default: 0.70 }] },
    { kind: "selfBuffActive", label: "내 버프 '__' 활성", group: "자기",
        params: [{ field: "buffId", label: "버프 id", kind: "text", default: "" }] },
    { kind: "selfDebuffActive", label: "내 디버프 '__' 활성", group: "자기",
        params: [{ field: "buffId", label: "디버프 id", kind: "text", default: "" }] },

    { kind: "enemyHpBelow", label: "적 HP 가 __% 이하", group: "적",
        params: [{ field: "value", label: "비율", kind: "ratio", default: 0.25 }] },
    { kind: "enemyHpAbove", label: "적 HP 가 __% 이상", group: "적",
        params: [{ field: "value", label: "비율", kind: "ratio", default: 0.75 }] },
    { kind: "enemyHasTag", label: "적이 '__' 속성", group: "적",
        params: [{ field: "tag", label: "태그", kind: "tagPicker", default: "boss",
            options: ["boss", "caster", "ranged", "beast", "human", "magical", "swarm"] }] },
    { kind: "enemyCount", label: "시야 적 __명", group: "적",
        params: [{ field: "amount", label: "최소", kind: "integer", default: 2 }] },
    { kind: "enemyCasting", label: "적이 시전 중", group: "적", params: [] },

    { kind: "distanceAbove", label: "거리 __ 초과", group: "거리",
        params: [{ field: "value", label: "거리", kind: "distance", default: 3.0 }] },
    { kind: "distanceBelow", label: "거리 __ 미만", group: "거리",
        params: [{ field: "value", label: "거리", kind: "distance", default: 2.0 }] },

    { kind: "tickFromStart", label: "전투 시작 후 __ tick 이상", group: "시간",
        params: [{ field: "amount", label: "tick", kind: "integer", default: 10 }] },
    { kind: "combatPhase", label: "전투 단계 '__'", group: "시간",
        params: [{ field: "tag", label: "단계", kind: "tagPicker", default: "mid",
            options: ["opening", "mid", "end"] }] },

    { kind: "cooldownReady", label: "스킬 '__' 쿨다운 완료", group: "자원",
        params: [{ field: "skillId", label: "스킬", kind: "skillPicker", default: "" }] },
    { kind: "allyHpBelow", label: "아군 최저 HP __% 이하", group: "자원",
        params: [{ field: "value", label: "비율", kind: "ratio", default: 0.35 }] },

    { kind: "and", label: "모두 참 (AND)", group: "조합", params: [], hint: "하위 조건들을 AND 로 묶음" },
    { kind: "or", label: "하나라도 참 (OR)", group: "조합", params: [] },
    { kind: "not", label: "뒤집기 (NOT)", group: "조합", params: [] },
];

export interface ActionSpec {
    kind: TacticActionKind;
    label: string;
    params: ActionParamSpec[];
    hint?: string;
}

export type ActionParamSpec =
    | { field: "skillId"; label: string; kind: "skillPicker"; default: string }
    | { field: "target"; label: string; kind: "targetPicker"; default: string }
    | { field: "ticks"; label: string; kind: "integer"; default: number };

export const ACTION_SPECS: ActionSpec[] = [
    { kind: "useSkill", label: "스킬 사용", params: [
        { field: "skillId", label: "스킬", kind: "skillPicker", default: "" },
        { field: "target", label: "대상", kind: "targetPicker", default: "currentTarget" },
    ] },
    { kind: "basicAttack", label: "기본 공격", params: [
        { field: "target", label: "대상", kind: "targetPicker", default: "currentTarget" },
    ] },
    { kind: "moveToward", label: "다가가기", params: [
        { field: "target", label: "대상", kind: "targetPicker", default: "currentTarget" },
    ] },
    { kind: "moveAway", label: "물러나기", params: [
        { field: "target", label: "기준", kind: "targetPicker", default: "currentTarget" },
    ] },
    { kind: "holdPosition", label: "자리 유지", params: [] },
    { kind: "defend", label: "방어 자세", params: [] },
    { kind: "retreat", label: "후퇴", params: [] },
    { kind: "wait", label: "대기", params: [
        { field: "ticks", label: "tick", kind: "integer", default: 3 },
    ] },
];

export const TARGET_OPTIONS: Array<{ value: string; label: string }> = [
    { value: "currentTarget", label: "현재 대상" },
    { value: "nearestEnemy", label: "가장 가까운 적" },
    { value: "lowestHpAlly", label: "최저 HP 아군" },
    { value: "self", label: "자신" },
];

/**
 * 한 조건을 사람이 읽을 수 있는 문장으로 요약.
 * 편집 중이 아닌 규칙 리스트 뷰에서 사용.
 */
export function summarizeCondition(cond: TacticCondition): string {
    switch (cond.kind) {
        case "always": return "항상";
        case "selfHpBelow": return `내 HP ${pct(cond.value)} 이하`;
        case "selfHpAbove": return `내 HP ${pct(cond.value)} 이상`;
        case "selfMpBelow": return `내 MP ${pct(cond.value)} 이하`;
        case "selfMpAbove": return `내 MP ${pct(cond.value)} 이상`;
        case "selfBuffActive": return `버프 "${cond.buffId ?? "?"}" 활성`;
        case "selfDebuffActive": return `디버프 "${cond.buffId ?? "?"}" 활성`;
        case "enemyHpBelow": return `적 HP ${pct(cond.value)} 이하`;
        case "enemyHpAbove": return `적 HP ${pct(cond.value)} 이상`;
        case "enemyHasTag": return `적 "${cond.tag ?? "?"}"`;
        case "enemyCount": return `시야 적 ${cond.amount ?? "?"}명 이상`;
        case "enemyCasting": return "적 시전 중";
        case "distanceAbove": return `거리 > ${cond.value ?? "?"}`;
        case "distanceBelow": return `거리 < ${cond.value ?? "?"}`;
        case "tickFromStart": return `${cond.amount ?? "?"} tick 경과`;
        case "combatPhase": return `단계 "${cond.tag ?? "?"}"`;
        case "cooldownReady": return `${cond.skillId ?? "?"} 준비`;
        case "allyHpBelow": return `아군 HP ${pct(cond.value)} 이하`;
        case "and":
            return `(${(cond.children ?? []).map(summarizeCondition).join(" AND ")})`;
        case "or":
            return `(${(cond.children ?? []).map(summarizeCondition).join(" OR ")})`;
        case "not":
            return `!${cond.inner ? summarizeCondition(cond.inner) : "?"}`;
    }
}

export function summarizeAction(act: TacticAction): string {
    const target = act.target ? targetLabel(act.target) : "";
    switch (act.kind) {
        case "useSkill": return `[${act.skillId ?? "?"}] ${target ? "→ " + target : ""}`;
        case "basicAttack": return `기본 공격 → ${target}`;
        case "moveToward": return `→ ${target}`;
        case "moveAway": return `${target} 에서 물러나기`;
        case "holdPosition": return "자리 유지";
        case "defend": return "방어";
        case "retreat": return "후퇴";
        case "wait": return `${act.ticks ?? "?"} tick 대기`;
    }
}

function targetLabel(value: string): string {
    return TARGET_OPTIONS.find(o => o.value === value)?.label ?? value;
}

function pct(v: number | undefined): string {
    return v === undefined ? "?" : `${Math.round(v * 100)}%`;
}

/** 스펙 기반 기본값으로 빈 조건을 만든다. */
export function buildDefaultCondition(kind: TacticConditionKind): TacticCondition {
    const spec = CONDITION_SPECS.find(s => s.kind === kind);
    const cond: TacticCondition = { kind };
    if (!spec) return cond;
    for (const p of spec.params) {
        switch (p.field) {
            case "value": cond.value = p.default; break;
            case "amount": cond.amount = p.default; break;
            case "skillId": cond.skillId = p.default; break;
            case "tag": cond.tag = p.default; break;
            case "buffId": cond.buffId = p.default; break;
        }
    }
    if (kind === "and" || kind === "or") cond.children = [];
    if (kind === "not") cond.inner = { kind: "always" };
    return cond;
}

export function buildDefaultAction(kind: TacticActionKind): TacticAction {
    const spec = ACTION_SPECS.find(s => s.kind === kind);
    const act: TacticAction = { kind };
    if (!spec) return act;
    for (const p of spec.params) {
        switch (p.field) {
            case "skillId": act.skillId = p.default; break;
            case "target": act.target = p.default; break;
            case "ticks": act.ticks = p.default; break;
        }
    }
    return act;
}
